import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

import type {
  AutomationSettings,
  DeepPartialAutomationSettings,
} from './email-account-automation-settings';

export const EMAIL_ACCOUNTS_QUERY_KEY = 'email-accounts';

export * from './email-account-automation-settings';

export interface EmailAccount {
  id: string;
  user_id: string;
  organization_id: string;
  // TODO(PUB-1055): widen to canonical `Provider` once the duplicate
  // EmailAccount interfaces + their consumers are widened together (cascades
  // 15+ sites). Until then, an IMAP account's runtime `'imap'` is narrowed at
  // the boundary via `ProviderSchema.safeParse` (see use-detail-panel-actions).
  provider: 'gmail' | 'outlook';
  // Curated IMAP preset discriminator ('yahoo' | 'icloud' | …); null for
  // gmail/outlook. Stored as text; resolves host/port server-side. Drives
  // per-preset branding without an email_metadata denormalisation. Optional
  // (not required) so the ~15 direct EmailAccount construction sites don't
  // need touching — that cascade is TODO(PUB-1055), not this change.
  imap_preset?: string | null;
  account_type: 'personal' | 'shared';
  email_address: string;
  display_name: string | null;
  avatar_url: string | null;
  gmail_signature: string | null;
  ai_prompt: string | null;
  gmail_watch_expiration: string | null;
  outlook_subscription_expiration: string | null;
  connection_status: 'connected' | 'disconnected';
  archive_on_assign: boolean;
  archive_on_send: boolean;
  archive_on_done: boolean;
  archive_on_no_response_required: boolean;
  /** Nullable in practice: production holds NULL for accounts predating the
   *  column's default, even though the migration declares it NOT NULL. Read
   *  through a `?? false` coercion — a raw null would drive an uncontrolled
   *  toggle and, on save, write null back. */
  archive_on_response_required: boolean | null;
  // Automation settings (JSONB column)
  automation_settings: AutomationSettings;
  // Per-account content storage toggle
  store_bodies: boolean;
  // Set once the account's initial backfill sync finishes; null while it runs.
  initial_sync_completed_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Unified hook to fetch all email accounts for a user in an organization
 * This replaces separate queries for Gmail and Outlook
 */
export function useEmailAccounts(organizationId?: string | null) {
  return useQuery({
    queryKey: [EMAIL_ACCOUNTS_QUERY_KEY, organizationId],
    queryFn: async (): Promise<EmailAccount[]> => {
      if (!organizationId) {
        return [];
      }
      // Not `getUser()`: auth-js runs that under the auth-token navigator lock
      // for a full GoTrue round-trip, and every other Supabase request waits on
      // that lock for its access token. `getSession()` reads memory unless a
      // refresh is due. A refresh failure MUST surface as a query error: the
      // client would otherwise fall back to the anon key, RLS would return no
      // rows, and an empty list would be cached as "no accounts".
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError) {
        throw sessionError;
      }
      if (!session) {
        return [];
      }

      // Explicit column allowlist — never select('*') on email_accounts:
      // the table contains access_token / refresh_token / scope and other
      // credentials we MUST NOT flow into the React Query cache (and from
      // there into React DevTools, network responses, and Sentry events).
      // See AGENTS.md "Frontend data exposure".
      // Keep this list synchronised with the EmailAccount interface above.
      const { data, error } = await supabase
        .from('email_accounts')
        .select(
          `
          id,
          user_id,
          organization_id,
          provider,
          imap_preset,
          account_type,
          email_address,
          display_name,
          avatar_url,
          gmail_signature,
          ai_prompt,
          gmail_watch_expiration,
          outlook_subscription_expiration,
          connection_status,
          archive_on_assign,
          archive_on_send,
          archive_on_done,
          archive_on_no_response_required,
          archive_on_response_required,
          automation_settings,
          store_bodies,
          initial_sync_completed_at,
          created_at,
          updated_at
        `,
        )
        .eq('organization_id', organizationId)
        .order('email_address', { ascending: true });

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || [];
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes - accounts rarely change
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache longer
    // Mount, focus and reconnect refetches are off app-wide (App.tsx query
    // defaults); connection status recovers through explicit invalidation.
  });
}

/**
 * Hook to get only connected email accounts (excludes disconnected)
 */
export function useConnectedEmailAccounts(
  organizationId?: string | null,
  options?: { enabled?: boolean },
) {
  const isEnabled = options?.enabled !== false;
  const { data, ...rest } = useEmailAccounts(isEnabled ? organizationId : null);
  // Depend on raw `data` (stable `undefined` when disabled/loading) instead of a
  // destructured default `[]`, which would be a fresh reference each render and
  // force `useMemo` to recompute.
  const connectedAccounts = useMemo(
    () => (data ?? []).filter((account) => account.connection_status === 'connected'),
    [data],
  );
  return { data: connectedAccounts, ...rest };
}

/**
 * Hook to get only disconnected email accounts
 */
export function useDisconnectedEmailAccounts(organizationId?: string | null) {
  const { data, ...rest } = useEmailAccounts(organizationId);
  const disconnectedAccounts = useMemo(
    () => (data ?? []).filter((account) => account.connection_status === 'disconnected'),
    [data],
  );
  return { data: disconnectedAccounts, ...rest };
}

/**
 * Hook to update an email account's AI prompt
 */
export function useUpdateEmailAccountPrompt(organizationId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      prompt,
    }: {
      accountId: string;
      prompt: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from('email_accounts')
        .update({ ai_prompt: prompt })
        .eq('id', accountId);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      // Invalidate email accounts cache to refetch with updated prompt
      await queryClient.invalidateQueries({
        queryKey: [EMAIL_ACCOUNTS_QUERY_KEY, organizationId],
      });
    },
  });
}

/**
 * Hook to update an email account's settings (archive and permissions)
 */
export function useUpdateEmailAccountArchiveSettings(organizationId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      archiveOnAssign,
      archiveOnSend,
      archiveOnDone,
      archiveOnNoResponseRequired,
      archiveOnToRespond,
    }: {
      accountId: string;
      archiveOnAssign: boolean;
      archiveOnSend: boolean;
      archiveOnDone: boolean;
      archiveOnNoResponseRequired: boolean;
      archiveOnToRespond: boolean;
      /** @deprecated No longer used — all org members can delete. Kept for V1 caller compat. */
      allowMemberDelete?: boolean;
    }): Promise<void> => {
      const { error } = await supabase
        .from('email_accounts')
        .update({
          archive_on_assign: archiveOnAssign,
          archive_on_send: archiveOnSend,
          archive_on_done: archiveOnDone,
          archive_on_no_response_required: archiveOnNoResponseRequired,
          archive_on_response_required: archiveOnToRespond,
        })
        .eq('id', accountId);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      // Invalidate email accounts cache to refetch with updated settings
      await queryClient.invalidateQueries({
        queryKey: [EMAIL_ACCOUNTS_QUERY_KEY, organizationId],
      });
    },
  });
}

/**
 * Hook to update an email account's automation settings
 * Uses JSONB merge to update only the specified fields
 */
export function useUpdateEmailAccountAutomationSettings(organizationId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      settings,
    }: {
      accountId: string;
      settings: DeepPartialAutomationSettings;
    }): Promise<void> => {
      // Atomic merge via RPC — no read-modify-write race
      const { error } = await supabase.rpc('update_email_account_automation_settings', {
        p_account_id: accountId,
        p_settings: settings as unknown as Record<string, unknown>,
      });

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      // Invalidate email accounts cache to refetch with updated settings
      await queryClient.invalidateQueries({
        queryKey: [EMAIL_ACCOUNTS_QUERY_KEY, organizationId],
      });
    },
  });
}

/**
 * Hook to update an email account's store_bodies setting
 */
export function useUpdateEmailAccountStoreBodies(organizationId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      storeBodies,
    }: {
      accountId: string;
      storeBodies: boolean;
    }): Promise<void> => {
      const { error } = await supabase
        .from('email_accounts')
        .update({ store_bodies: storeBodies })
        .eq('id', accountId);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [EMAIL_ACCOUNTS_QUERY_KEY, organizationId],
      });
    },
  });
}
